/** Load private narration without putting credentials in a media URL. */
export class NarrationPlaybackError extends Error {}

export async function loadNarrationPlayback(input: {
  audioUrl: string;
  generationId: string;
  accessToken?: string;
  guestAccessToken?: string;
  signal: AbortSignal;
}, fetcher: typeof fetch = fetch, urls = URL) {
  if (!/^\/api\/ai\/narration\/[a-f0-9]{40}$/.test(input.audioUrl)) throw new NarrationPlaybackError('Invalid narration address. This saved lesson has no usable original audio reference.');
  const headers: Record<string, string> = { 'X-Lavyn-Generation-Id': input.generationId };
  if (input.accessToken) headers.Authorization = `Bearer ${input.accessToken}`;
  else if (input.guestAccessToken) headers['X-Lavyn-Audio-Token'] = input.guestAccessToken;
  else throw new NarrationPlaybackError('Narration authorization is unavailable. Sign in to the account that created this lesson.');
  let response: Response;
  try {
    response = await fetcher(input.audioUrl, { headers, signal: input.signal, cache: 'no-store', credentials: 'same-origin' });
  } catch (error) {
    if (input.signal.aborted) throw error;
    throw new NarrationPlaybackError('Could not connect to load narration. Check your connection and try again.');
  }
  if (!response.ok) {
    const details = await response.json().catch(() => null) as { code?: string } | null;
    const message = response.status === 401
      ? 'Your session has expired. Please sign in again to load the original narration.'
      : response.status === 403
        ? 'This session is not authorized to load this narration.'
        : response.status >= 500
          ? 'The server could not load narration. Please try again.'
          : details?.code === 'NARRATION_GUEST_EXPIRED'
            ? 'Guest narration access has expired. It cannot be renewed without verified ownership.'
            : details?.code === 'NARRATION_FILE_MISSING'
              ? 'The original narration file is missing from this server.'
              : 'This server cannot find or verify the original narration for this account.';
    throw new NarrationPlaybackError(message);
  }
  const blob = await response.blob();
  if (input.signal.aborted) throw new DOMException('Narration loading was cancelled.', 'AbortError');
  const url = urls.createObjectURL(blob);
  let released = false;
  const dispose = () => {
    if (!released) { released = true; urls.revokeObjectURL(url); }
    input.signal.removeEventListener('abort', dispose);
  };
  input.signal.addEventListener('abort', dispose, { once: true });
  return { url, dispose };
}

/** Fetch one owner-checked PCM segment for the progressive Web Audio clock. */
export async function loadNarrationPcmSegment(input: {
  audioUrl: string;
  generationId: string;
  accessToken?: string;
  signal: AbortSignal;
}, fetcher: typeof fetch = fetch) {
  if (!/^\/api\/ai\/narration-segment\/[a-f0-9]{40}$/.test(input.audioUrl)) {
    throw new NarrationPlaybackError('Invalid progressive narration address.');
  }
  if (!input.accessToken) {
    throw new NarrationPlaybackError('Sign in to the account that created this lesson to load its narration.');
  }
  const headers = {
    Authorization: `Bearer ${input.accessToken}`,
    'X-Lavyn-Generation-Id': input.generationId,
  };
  let response: Response;
  try {
    response = await fetcher(input.audioUrl, {
      headers,
      signal: input.signal,
      cache: 'no-store',
      credentials: 'same-origin',
    });
  } catch (error) {
    if (input.signal.aborted) throw error;
    throw new NarrationPlaybackError('Could not connect to load the next narration segment.');
  }
  if (!response.ok) {
    const details = await response.json().catch(() => null) as { code?: string } | null;
    throw new NarrationPlaybackError(
      response.status === 401
        ? 'Your session has expired. Sign in again to continue this lesson.'
        : response.status >= 500
          ? 'The server could not load the next narration segment. Please try again.'
          : details?.code === 'NARRATION_FILE_MISSING'
            ? 'A saved narration segment is missing.'
            : 'This account cannot access the next narration segment.',
    );
  }
  const bytes = await response.arrayBuffer();
  if (input.signal.aborted) throw new DOMException('Narration loading was cancelled.', 'AbortError');
  if (!bytes.byteLength || bytes.byteLength % 2 !== 0 || bytes.byteLength > 12_000_000) {
    throw new NarrationPlaybackError('The progressive narration segment is invalid.');
  }
  return bytes;
}
